import type { OBB, Vec2 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import { obbCorners } from '../../geo/polygon';
import type { Lot } from './types';
import { across } from './acrossPlot';
import { lotFrame } from './lotFrame';
import { lotKey } from './lotKey';
import type { BankLine, Plot } from './plots';
import type { LotPlacer } from './lotPlacer';
import { LOT } from './lotRules';
import { widen, type PlotGround } from './widenAcross';

export interface BacklotInput {
  /** The plots to build on, one per house. */
  readonly plots: readonly Plot[];
  /** The plots roofed through, from the courtyard to the back line or to the warehouse on the water. */
  readonly through?: ReadonlySet<Plot>;
  /** Knows every lot placed so far; learns each building placed here. */
  readonly placer: LotPlacer;
  /** Where the sources show a yard open: no building there is shortened to fit. */
  readonly inYard?: InYard;
  /** The ground every plot takes: a building across its plot widens onto free ground outside all of them. */
  readonly ground?: PlotGround;
  readonly rng: Rng;
  readonly startIndex: number;
}

export interface BacklotOutput {
  readonly backlots: readonly Lot[];
}

/**
 * PLAN Lots: behind the house a small courtyard (plaats), then the back house across the plot; a plot
 * with room left for a yard and another building has a warehouse or workshop on its back line. The
 * courtyard holds the privy: cesspits of 1.5 to 2 m stood on the back ground of the Oudezijds Armsteeg
 * lots in 1450-1575 (AAR 60). Back buildings on those lots measured 6.50 by 4.10 m (AAR 60); back
 * houses stood behind Zeedijk 30 and Warmoesstraat 145 by 1560 (Van Tussenbroek 2009), and back houses,
 * warehouses and workshops filled the Warmoesstraat block after 1400 (Nikkels 2013).
 */
export const BACK = {
  courtyard: { min: 2.5, max: 4 },
  depth: { min: 4, max: 8 },
  yard: 2,
  /** Where plots fan in on a bend, a building that does not fit across the plot tries these shares of it. */
  narrow: [0.75, 0.5],
  /**
   * A building whose span meets an earlier building, where the plots of two streets cross, takes the
   * longest part of its span left free, found in `step`s and no shallower than the least back
   * building, rather than none; narrowed, it may keep to either side of the plot.
   */
  free: { step: 0.5 },
  /**
   * A warehouse on a bank shortens, or pulls its back in from a slanting bank, by `step`, down to
   * `least` deep. It stands square to the bank where the bank line turns from the plot's by up to
   * `maxTurn`, and so is no more than 1.4 times its house's width; where the bank bends, it takes
   * these shares of the line, kept to either end, to clear its neighbour.
   */
  bank: { step: 0.5, least: 2, maxTurn: Math.PI / 4, narrow: [0.9, 0.75] },
} as const;

/** Whether a point lies in a yard the sources show open, where no building is shortened to fit. */
export type InYard = (p: Vec2) => boolean;

interface Width {
  readonly share: number;
  readonly shift: number;
}

/* The whole plot, then each narrowed share in the plot's middle and kept to either side. */
function widthsOf(house: Lot): readonly Width[] {
  const { width } = lotFrame(house);
  return [
    { share: 1, shift: 0 },
    ...BACK.narrow.flatMap((share) => {
      const off = ((1 - share) * width) / 2;
      return [0, -off, off].map((shift) => ({ share, shift }));
    }),
  ];
}

interface Run {
  readonly from: number;
  readonly to: number;
}

type Local = readonly [number, number];

/* A point in the plot's terms: across it from its middle line, and behind the house's back wall. */
function inPlot(house: Lot): (p: Vec2) => Local {
  const { frontMid, facing, depth } = lotFrame(house);
  const [ax, ay] = house.obb.axisU;
  return (p) => [
    (p[0] - frontMid[0]) * ax + (p[1] - frontMid[1]) * ay,
    (frontMid[0] - p[0]) * facing[0] + (frontMid[1] - p[1]) * facing[1] - depth,
  ];
}

/* What of a polygon lies on the kept side of the line across the plot at `at`. */
function clipAt(points: readonly Local[], at: number, keep: (u: number) => boolean): Local[] {
  return points.flatMap((p, i): Local[] => {
    const q = points[(i + 1) % points.length]!;
    const cut: Local[] =
      keep(p[0]) === keep(q[0]) ? [] : [[at, p[1] + ((at - p[0]) / (q[0] - p[0])) * (q[1] - p[1])]];
    return keep(p[0]) ? [p, ...cut] : cut;
  });
}

/*
 * The depths behind the house a lot takes within a band of the plot; a lot reaching less than the
 * overlap lots may have into the band's sides leaves it free, so neighbours stand wall to wall.
 */
function blockedDepths(ring: readonly Local[], lo: number, hi: number): Run | null {
  const tol = LOT.overlapTolerance;
  const inside = clipAt(
    clipAt(ring, lo + tol, (u) => u >= lo + tol),
    hi - tol,
    (u) => u <= hi - tol
  );
  if (inside.length === 0) return null;
  const depths = inside.map((p) => p[1]);
  const run = { from: Math.min(...depths), to: Math.max(...depths) };
  return run.to > run.from ? run : null;
}

/* The longest stretch of the span no lot takes, as deep as the least back building. */
function longestFree(span: Run, blocked: readonly Run[]): Run | null {
  let best: Run | null = null;
  let at = span.from;
  for (const b of [...blocked, { from: span.to, to: span.to }].sort((x, y) => x.from - y.from)) {
    const run = { from: at, to: Math.min(b.from, span.to) };
    if (run.to - run.from > (best ? best.to - best.from : 0)) best = run;
    at = Math.max(at, b.to);
  }
  return best && best.to - best.from >= BACK.depth.min - 1e-9 ? best : null;
}

/* The widest-by-deepest free part of the span, shortened from its far end until it fits whole. */
function freePart(house: Lot, span: Run, placer: LotPlacer): OBB | null {
  const local = inPlot(house);
  const rings = placer
    .blockers(across(house, span.from, span.to, 1))
    .map((ring) => ring.map(local));
  const { width } = lotFrame(house);
  const parts = widthsOf(house)
    .flatMap((at) => {
      const half = (at.share * width) / 2;
      const blocked = rings.flatMap(
        (r) => blockedDepths(r, at.shift - half, at.shift + half) ?? []
      );
      const run = longestFree(span, blocked);
      return run ? [{ at, run, area: at.share * (run.to - run.from) }] : [];
    })
    .sort((a, b) => b.area - a.area);
  for (const { at, run } of parts) {
    for (let to = run.to; to - run.from >= BACK.depth.min - 1e-9; to -= BACK.free.step) {
      const obb = across(house, run.from, to, at.share, at.shift);
      if (placer.fits(obb)) return obb;
    }
  }
  return null;
}

/**
 * A building across the plot from `from` to `to` behind the house: the whole span, narrowed where
 * the plot fans in; else the free part of it, unless its middle lies in a yard the sources show.
 */
export function placeAcross(
  house: Lot,
  from: number,
  to: number,
  placer: LotPlacer,
  inYard: InYard
): OBB | null {
  const whole = [1, ...BACK.narrow]
    .map((share) => across(house, from, to, share))
    .find((obb) => placer.fits(obb));
  if (whole) return whole;
  const part = freePart(house, { from, to }, placer);
  return part && !inYard(part.center) ? part : null;
}

/** The lots standing so far, and the yards the sources show open. */
interface Placing {
  readonly placer: LotPlacer;
  readonly inYard: InYard;
}

/** Where a building stands and which way its front looks. */
interface Placed {
  readonly obb: OBB;
  readonly facing: Vec2;
}

function building(house: Lot, sourceId: string, placed: Placed, index: number): Lot {
  const { obb, facing } = placed;
  return {
    index,
    sourceId,
    side: house.side,
    ordinal: house.ordinal,
    kind: 'backhouse',
    obb,
    corners: obbCorners(obb),
    frontage: obb.halfU * 2,
    depth: obb.halfV * 2,
    facing,
  };
}

/** The back house behind the courtyard, and where it ends; `end` is 0 when none fits the plot. */
function buildBack(
  plot: Plot,
  rng: Rng,
  at: Placing
): { readonly obb: OBB | null; readonly end: number } {
  const key = lotKey(plot.lot);
  const courtyard = rng.fork(`yard:${key}`).range(BACK.courtyard.min, BACK.courtyard.max);
  if (plot.depth < courtyard + BACK.depth.min) return { obb: null, end: 0 };
  const end =
    courtyard +
    Math.min(rng.fork(`dim:${key}`).range(BACK.depth.min, BACK.depth.max), plot.depth - courtyard);
  const obb = placeAcross(plot.lot, courtyard, end, at.placer, at.inYard);
  return { obb, end: obb ? behindHouse(plot.lot, obb) + obb.halfV * 2 : end };
}

/*
 * A plot roofed through: from the courtyard to `to`, the back house, then back houses, warehouses and
 * workshops wall to wall, each drawn as deep as a back house, one that would leave less than the least
 * depth behind it running on to `to`; so the roofs run on unbroken through the block, as the woodcut
 * of 1544 shows the Warmoesstraat's lots roofed from the street to the Damrak. The back house takes the
 * draws it takes on a plot not roofed through. `place` gets each building and its place in the row.
 */
function buildThrough(
  plot: Plot,
  to: number,
  rng: Rng,
  at: Placing,
  place: (obb: OBB | null, k: number) => void
): void {
  const key = lotKey(plot.lot);
  let start = rng.fork(`yard:${key}`).range(BACK.courtyard.min, BACK.courtyard.max);
  for (let k = 0; to - start >= BACK.depth.min; k++) {
    const draw = rng.fork(k === 0 ? `dim:${key}` : `mid${k}:${key}`);
    const drawn = draw.range(BACK.depth.min, BACK.depth.max);
    const end = to - start - drawn < BACK.depth.min ? to : start + drawn;
    place(placeAcross(plot.lot, start, end, at.placer, at.inYard), k);
    start = end;
  }
}

/** How far behind its house's back wall a building's nearest corner stands. */
function behindHouse(house: Lot, obb: OBB): number {
  const { frontMid, facing, depth } = lotFrame(house);
  const behind = (p: Vec2) => (frontMid[0] - p[0]) * facing[0] + (frontMid[1] - p[1]) * facing[1];
  return Math.min(...obbCorners(obb).map(behind)) - depth;
}

/** A warehouse or workshop on the plot's back line, a yard behind the back house. */
function buildRear(plot: Plot, backEnd: number, rng: Rng, at: Placing): OBB | null {
  const room = plot.depth - Math.max(backEnd, BACK.courtyard.max) - BACK.yard;
  if (room < BACK.depth.min) return null;
  const depth = Math.min(
    rng.fork(`rear:${lotKey(plot.lot)}`).range(BACK.depth.min, BACK.depth.max),
    room
  );
  return placeAcross(plot.lot, plot.depth - depth, plot.depth, at.placer, at.inYard);
}

/** A warehouse's ground square to a bank line: where the line starts, its axes, and how deep it may run. */
interface BankFrame {
  readonly left: Vec2;
  readonly axisU: Vec2;
  /** From the water toward the house. */
  readonly inward: Vec2;
  readonly width: number;
  /** The depth at which its front corner nearer the house reaches the courtyard. */
  readonly room: number;
}

function bankFrame(house: Lot, line: BankLine, courtyard: number): BankFrame | null {
  const { frontMid, facing, depth } = lotFrame(house);
  const run: Vec2 = [line.right[0] - line.left[0], line.right[1] - line.left[1]];
  const width = Math.hypot(run[0], run[1]);
  if (width === 0) return null;
  const axisU: Vec2 = [run[0] / width, run[1] / width];
  const cos = -axisU[1] * facing[0] + axisU[0] * facing[1];
  if (Math.abs(cos) < Math.cos(BACK.bank.maxTurn)) return null;
  const back: Vec2 = [frontMid[0] - facing[0] * depth, frontMid[1] - facing[1] * depth];
  const behind = (p: Vec2) => (back[0] - p[0]) * facing[0] + (back[1] - p[1]) * facing[1];
  return {
    left: line.left,
    axisU,
    inward: cos >= 0 ? [-axisU[1], axisU[0]] : [axisU[1], -axisU[0]],
    width,
    room: (Math.min(behind(line.left), behind(line.right)) - courtyard) / Math.abs(cos),
  };
}

/* The stretches of the bank line a warehouse may take: all of it, then shorter ones kept to either end. */
function stretches(frame: BankFrame): readonly { readonly middle: Vec2; readonly width: number }[] {
  const { left, axisU, width } = frame;
  return [1, ...BACK.bank.narrow].flatMap((share) => {
    const w = width * share;
    return (share === 1 ? [0] : [0, width - w]).map((start) => ({
      middle: [left[0] + axisU[0] * (start + w / 2), left[1] + axisU[1] * (start + w / 2)] as Vec2,
      width: w,
    }));
  });
}

/*
 * Square to the bank line with its rear wall on it, so a row of them lines the water; one that meets
 * a neighbour shortens, then takes less of the line, its rear wall kept on the line.
 */
function onBankLine(frame: BankFrame, drawn: number, placer: LotPlacer): Placed | null {
  const { axisU, inward } = frame;
  for (const { middle, width } of stretches(frame)) {
    for (let d = Math.min(drawn, frame.room); d >= BACK.bank.least; d -= BACK.bank.step) {
      const obb: OBB = {
        center: [middle[0] + (inward[0] * d) / 2, middle[1] + (inward[1] * d) / 2],
        axisU,
        halfU: width / 2,
        halfV: d / 2,
      };
      if (placer.fits(obb)) return { obb, facing: inward };
    }
  }
  return null;
}

/**
 * On a bank plot the warehouse stands on the water, a courtyard before it: the Warmoesstraat's
 * spijkers, house, office and warehouse in one, lay on the water of the Damrak (Nikkels 2013, after
 * the tax registers of 1543), and its lots run to the Damrak on Anthonisz's plan of 1544. Where the
 * bank line is unknown or its warehouse does not fit, it stands square to the plot, its back pulled
 * in from a slanting bank until its corners stand on land.
 */
function buildBank(plot: Plot, rng: Rng, at: Placing): Placed | null {
  const key = lotKey(plot.lot);
  const courtyard = rng.fork(`yard:${key}`).range(BACK.courtyard.min, BACK.courtyard.max);
  const drawn = rng.fork(`dim:${key}`).range(BACK.depth.min, BACK.depth.max);
  const frame = plot.bankLine && bankFrame(plot.lot, plot.bankLine, courtyard);
  const aligned = frame && onBankLine(frame, drawn, at.placer);
  if (aligned) return aligned;
  const from = plot.depth - Math.min(drawn, plot.depth - courtyard);
  for (let to = plot.depth; to - from >= BACK.bank.least; to -= BACK.bank.step) {
    const obb = placeAcross(plot.lot, from, to, at.placer, at.inYard);
    if (obb) return { obb, facing: plot.lot.facing };
  }
  return null;
}

/* Every draw forks on the house's name, so a plot's buildings stay put whatever other plots there are. */
export function generateBacklots(input: BacklotInput): BacklotOutput {
  const { plots, through = new Set<Plot>(), placer, rng, startIndex } = input;
  const at: Placing = { placer, inYard: input.inYard ?? (() => false) };
  const backlots: Lot[] = [];
  const place = (house: Lot, sourceId: string, placed: Placed | null) => {
    if (!placed) return;
    const lot = building(house, sourceId, placed, startIndex + backlots.length);
    backlots.push(lot);
    placer.add(lot);
  };
  const { ground } = input;
  const fits = (obb: OBB) => placer.fits(obb);
  const wide = (house: Lot, obb: OBB) =>
    ground ? widen(obb, house, { fits, ground, inYard: at.inYard }) : obb;
  const square = (house: Lot, obb: OBB | null) =>
    obb && { obb: wide(house, obb), facing: house.facing };
  const roof = (plot: Plot, to: number) => {
    const { sourceId } = plot.lot;
    buildThrough(plot, to, rng, at, (obb, k) => {
      place(plot.lot, k === 0 ? sourceId : `${sourceId}/mid${k}`, square(plot.lot, obb));
    });
  };
  for (const plot of plots) {
    const rear = `${plot.lot.sourceId}/rear`;
    if (plot.bank) {
      const warehouse = buildBank(plot, rng, at);
      place(plot.lot, rear, warehouse);
      if (through.has(plot))
        roof(plot, warehouse ? behindHouse(plot.lot, warehouse.obb) : plot.depth);
      continue;
    }
    if (through.has(plot)) {
      roof(plot, plot.depth);
      continue;
    }
    const back = buildBack(plot, rng, at);
    place(plot.lot, plot.lot.sourceId, square(plot.lot, back.obb));
    place(plot.lot, rear, square(plot.lot, buildRear(plot, back.end, rng, at)));
  }
  return { backlots };
}
